'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  Container,
  Typography,
  Skeleton,
  Stack,
  Chip,
  Link as MuiLink,
} from '@mui/material';
import { MenuBook, OpenInNew } from '@mui/icons-material';

import { api } from '@/lib/api';
import { withOpacity } from '@/lib/colors';
import { useColorTheme } from '@/components/ColorModeProvider';
import PageHero from '@/components/PageHero';
import type { Publication } from '@/types';

// The citation field is a rich-text blob (Strapi stores markdown-ish text).
// On this page we want the plain APA-style string verbatim, so strip the
// inline markdown markers without otherwise altering the text.
const stripMarkdown = (text?: string) =>
  (text ?? '')
    .replace(/[*_`~]/g, '')
    .replace(/<[^>]*>/g, '')
    .replace(/\n+/g, ' ')
    .trim();

const PublicationsPage: React.FC = () => {
  const colors = useColorTheme();
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await api.getPublications();
        if (!cancelled) {
          setPublications(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load publications');
          setPublications([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Group by year, newest first. Within a year keep server order (createdAt desc).
  const grouped = useMemo(() => {
    const byYear = new Map<string, Publication[]>();
    for (const pub of publications) {
      const list = byYear.get(pub.publicationYear) ?? [];
      list.push(pub);
      byYear.set(pub.publicationYear, list);
    }
    return Array.from(byYear.entries()).sort(([a], [b]) => b.localeCompare(a));
  }, [publications]);

  // Offset sticky nav (fixed AppBar 72px + NavigationBar) when anchoring to a year.
  const SCROLL_OFFSET = 140;

  const [activeYear, setActiveYear] = useState<string | null>(null);
  const isProgrammaticScroll = useRef(false);

  useEffect(() => {
    if (grouped.length === 0) return;
    setActiveYear((prev) => prev ?? grouped[0][0]);

    const observer = new IntersectionObserver(
      (entries) => {
        if (isProgrammaticScroll.current) return;
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const topmost = visible.reduce((a, b) =>
          a.boundingClientRect.top < b.boundingClientRect.top ? a : b
        );
        setActiveYear(topmost.target.id.replace('year-', ''));
      },
      { rootMargin: `-${SCROLL_OFFSET}px 0px -55% 0px`, threshold: 0 }
    );

    grouped.forEach(([year]) => {
      const el = document.getElementById(`year-${year}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [grouped]);

  const handleYearClick = useCallback((year: string) => {
    const el = document.getElementById(`year-${year}`);
    if (!el) return;
    isProgrammaticScroll.current = true;
    setActiveYear(year);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 700);
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', background: colors.background.default }}>
      <PageHero
        label="Research"
        title="Publications"
        subtitle="Peer-reviewed papers, technical reports, and research outputs from the NSERC CREATE Cybersecurity Program."
        icon={<MenuBook />}
      />

      <Box sx={{ position: 'relative' }}>
      {grouped.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            left: 32,
            top: 0,
            bottom: 0,
            display: { xs: 'none', lg: 'block' },
            zIndex: 5,
          }}
        >
        <Box
          component="nav"
          aria-label="Publication years"
          sx={{
            position: 'sticky',
            top: `${SCROLL_OFFSET}px`,
            height: `calc(100vh - ${SCROLL_OFFSET}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          {grouped.map(([year], idx) => {
            const isActive = activeYear === year;
            return (
              <Box
                key={year}
                sx={{ position: 'relative', display: 'flex', alignItems: 'center', pointerEvents: 'auto' }}
              >
                <ButtonBase
                  onClick={() => handleYearClick(year)}
                  focusRipple
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    pr: 1.5,
                    borderRadius: 1,
                    '&:hover .timeline-dot': {
                      transform: 'scale(1.4)',
                      background: colors.brand.accent.main,
                    },
                    '&:hover .timeline-label': {
                      color: colors.brand.accent.dark,
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: 16,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {idx < grouped.length - 1 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: '1.5px',
                          height: 'calc(100% + 16px)',
                          background: colors.border.primary,
                          transform: 'translateX(-50%)',
                          zIndex: 0,
                        }}
                      />
                    )}
                    <Box
                      className="timeline-dot"
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        width: isActive ? 12 : 9,
                        height: isActive ? 12 : 9,
                        borderRadius: '50%',
                        background: isActive
                          ? colors.brand.accent.main
                          : colors.background.default,
                        border: `2px solid ${
                          isActive ? colors.brand.accent.main : colors.border.primary
                        }`,
                        transition: 'all 0.2s ease',
                      }}
                    />
                  </Box>
                  <Typography
                    className="timeline-label"
                    sx={{
                      color: isActive ? colors.brand.accent.dark : colors.text.secondary,
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.85rem',
                      opacity: isActive ? 1 : 0.7,
                      transition: 'all 0.2s ease',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {year}
                  </Typography>
                </ButtonBase>
              </Box>
            );
          })}
        </Box>
        </Box>
      )}

      <Container maxWidth="md" sx={{ py: 6 }}>
        {loading ? (
          <Stack spacing={2}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={64}
                sx={{ bgcolor: withOpacity(colors.text.primary, 0.08), borderRadius: 1 }}
              />
            ))}
          </Stack>
        ) : error ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              background: colors.gradients.card,
              border: `1px solid ${colors.border.primary}`,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 1 }}>
              Unable to load publications
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              {error}
            </Typography>
          </Box>
        ) : publications.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              background: colors.gradients.card,
              border: `1px solid ${colors.border.primary}`,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 1 }}>
              No publications yet
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              Publications will appear here as they are added to the system.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={5}>
            {grouped.map(([year, pubs]) => (
              <Box key={year} id={`year-${year}`} sx={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: colors.brand.accent.dark,
                    fontWeight: 700,
                    mb: 2.5,
                    fontSize: { xs: '1.4rem', md: '1.6rem' },
                    borderLeft: `3px solid ${colors.brand.accent.main}`,
                    pl: 2,
                  }}
                >
                  {year}
                </Typography>

                <Stack
                  component="ol"
                  spacing={2.5}
                  sx={{ listStyle: 'none', m: 0, p: 0 }}
                >
                  {pubs.map((pub) => (
                    <Box
                      key={pub.id}
                      component="li"
                      sx={{
                        pl: 2,
                        borderLeft: `1px solid ${colors.border.primary}`,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.text.primary,
                          lineHeight: 1.7,
                          fontSize: { xs: '0.95rem', md: '1rem' },
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {stripMarkdown(pub.citation) || pub.title}
                      </Typography>

                      {(pub.externalLink || (pub.researchThemes && pub.researchThemes.length > 0)) && (
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          sx={{ mt: 1, rowGap: 1, alignItems: 'center' }}
                        >
                          {pub.researchThemes?.map((theme) => (
                            <Chip
                              key={theme.id}
                              label={theme.title}
                              size="small"
                              sx={{
                                background: withOpacity(colors.brand.accent.main, 0.12),
                                color: colors.brand.accent.dark,
                                fontWeight: 500,
                                fontSize: '0.7rem',
                              }}
                            />
                          ))}
                          {pub.externalLink && (
                            <MuiLink
                              href={pub.externalLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: colors.brand.accent.dark,
                                fontSize: '0.85rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              External link
                              <OpenInNew sx={{ fontSize: '0.85rem' }} />
                            </MuiLink>
                          )}
                        </Stack>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Container>
      </Box>
    </Box>
  );
};

export default PublicationsPage;
